import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api/phx";
import { useCountries } from "../../hooks/useCountries";
import { Select, Textfield } from "../Input/Input";
import PostcodeLookup from "../PostcodeLookup/PostcodeLookup";
import StandardFieldset from "../UI/StandardFieldset";
import StandardH2 from "../UI/StandardH2";
import { getLanguageCode } from "../../utils/getURLParams";
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { getDomainConfig } from "../../config/config";
import { ABContext } from "../../contexts/ab-context";

const BillingAddress = (props) => {
  const config = getDomainConfig();
  const [selectedCountryBilling, setSelectedCountryBilling] = useState("");
  const [countiesBilling, setCountiesBilling] = useState([]);

  // CRO-1381-checkout-redesign
  const abContext = useContext(ABContext);
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  const { isSuccess: countriesIsSuccess, data: countriesData } = useCountries();

  const { t } = useTranslation('common');

  const [countiesLoading, setCountiesLoading] = useState(true);

  const retrieveCounties = (selectedCountry) => {
      setCountiesLoading(true);
      API.get(`/regions?country=${selectedCountry}`).then((result) => {
        const counties = result.data.data.map((county) => {
          return (
            <option key={county.id} value={county.title}>
                {county.title}
              </option>
            );
          });
          setCountiesBilling(counties);
          setSelectedCountryBilling(selectedCountry);
          setCountiesLoading(false);
      });
  };

  let countries = [];
  if (countriesIsSuccess) {
    countries = countriesData.data.map((country) => {
      return (
        <option key={country.id} value={country.title}>
          {country.title}
        </option>
      );
    });
  }

  const handleCountryBillingChange = (event) => {
    props.resetField("billing-address-region")
    setSelectedCountryBilling(event.target.value)
    setCountiesBilling([])
    retrieveCounties(event.target.value);
  };

  // Manual button/field states
  const [showManualFields, setShowManualFields] = useState(false);
  const [showManualButton, setShowManualButton] = useState(true);
  const [isManualActive, setIsManualActive] = useState(false);

  const handleManualButtonClick = () => {
    setIsManualActive(true);
    setShowManualButton(false);
  };

  const hasErrors = Object.keys(props.errors).some(key => key.startsWith('billing-address'));

  useEffect(() => {
    if (hasErrors) {
      setIsManualActive(true);
      setShowManualButton(false);
    }
  }, [hasErrors]);

  useEffect(() => {
    const countryGeo = getLanguageCode("Atd");

    let defaultCountry = "";
    switch(countryGeo) {
      case "us":
        defaultCountry = "United States";
        break;
      case "en-nl":
        defaultCountry = "Netherlands";
        break;
      case "au":
        defaultCountry = "Australia";
        break;
      case "en-ie":
        defaultCountry = "Ireland";
        break;
      case "de":
        defaultCountry = "Germany";
        break;
      default:
        defaultCountry = "United Kingdom";
    }
    setSelectedCountryBilling(defaultCountry)
    retrieveCounties(defaultCountry);
  }, [])

  useEffect(() => {
    props.getCountry(selectedCountryBilling)
  },[selectedCountryBilling])

  const handleAddressSelect = () => {
    setShowManualButton(false);
    setIsManualActive(true);
  };
  // End script

  useEffect(() => {
    const checkAutofill = () => {
  
      if (!selectedCountryBilling) return;
  
      const billingFields = [
        'billing-address-street1',
        'billing-address-city', 
        'billing-address-postal-code'
      ];
  
      const hasAutofilledValues = billingFields.some(fieldName => {
        const value = props.getValues(fieldName);
        return value && value.length > 0;
      });
  
      if (hasAutofilledValues && isCro1381) {
        setShowManualButton(false);
        setIsManualActive(true);
      }
    };
  
    checkAutofill();
  }, [props.getValues, isCro1381, selectedCountryBilling]);

  return (
    <React.Fragment>
      <StandardFieldset id="billing-pane">
        <StandardH2 className={isCro1381 && ("cro1381H2")}>{t('billing-address')}</StandardH2>

        {(selectedCountryBilling === "United Kingdom" || getLanguageCode("ATD") === "en") && (
          <PostcodeLookup
            register={props.register}
            errors={props.errors}
            inputSize={8}
            getValues={props.getValues}
            address={"billing"}
            setValue={props.setValue}
            maskingClass='gb-mask'
            control={props.control}
            dataLayerInput={() => googleDataLayer.createUserClickInputEvent("postcode-lookup")}
            onAddressSelect={handleAddressSelect}
            data-testid="postcode-lookup"
          />
        )}

        {!isCro1381 &&
          <Textfield
            register={props.register}
            errors={props.errors}
            inputSize={32}
            required={true}
            name={"billing-address-fname"}
            label={t("first-name")}
            errorText={t('please-enter-a-name')}
            setValue={props.setValue}
            touched={props.touchedFields["billing-address-fname"]}
            maskingClass='gb-mask'
            autocomplete={'given-name'}
            dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-fname")}
          />
        }

        {!isCro1381 &&
          <Textfield
            register={props.register}
            errors={props.errors}
            inputSize={32}
            required={true}
            name={"billing-address-lname"}
            label={t("last-name")}
            errorText={t('please-enter-a-name')}
            setValue={props.setValue}
            touched={props.touchedFields["billing-address-lname"]}
            maskingClass='gb-mask'
            autocomplete={'family-name'}
            dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-lname")}
          />
        }

        {isCro1381 &&
        showManualButton &&
        (selectedCountryBilling === "United Kingdom" || getLanguageCode("ATD") === "en") &&
        !hasErrors && (
          <button
            className="addressManual"
            id="manualAddressButton"
            onClick={handleManualButtonClick}
          >
            {t('enter-address-manually')}
          </button>
        )}

        <div 
          className={`manualFields ${(isManualActive || !isCro1381 || !(selectedCountryBilling === "United Kingdom" || getLanguageCode("ATD") === "en") || hasErrors) ? 'active' : ''}`}
          data-testid="manual-fields"
        >
          <div className={isCro1381 && ("cro1381HalfForm")}>
            <Textfield
              register={props.register}
              errors={props.errors}
              inputSize={32}
              required={true}
              name={"billing-address-street1"}
              {...(!isCro1381 ? { label: t('street-address') } : { label: t('address-line-1') })}
              errorText={t('please-enter-a-street')}
              setValue={props.setValue}
              touched={props.touchedFields["billing-address-street1"]}
              maskingClass='gb-mask'
              autocomplete={'street-address address-line1'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-street1")}
            />

            <Textfield
              register={props.register}
              errors={props.errors}
              inputSize={32}
              {...(isCro1381 && { label: t('address-line-2-optional') })}
              name={"billing-address-street2"}
              touched={props.touchedFields["billing-address-street2"]}
              setValue={props.setValue}
              maskingClass='gb-mask'
              autocomplete={'street-address address-line2'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-street2")}
            />

            <Textfield
              register={props.register}
              errors={props.errors}
              inputSize={32}
              required={true}
              name={"billing-address-city"}
              label={t('city')}
              errorText={t('please-enter-a-city')}
              setValue={props.setValue}
              touched={props.touchedFields["billing-address-city"]}
              maskingClass='gb-mask'
              autocomplete={'address-level1'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-city")}
            />

          {countiesBilling.length > 0 && (
            <Select
              initialOption={t("please-select")}
              showInitialOption={true}
              register={props.register}
              errors={props.errors}
              inputSize={32}
              required={true}
              name={"billing-address-region"}
              label={t('county')}
              options={countiesBilling}
              setValue={props.setValue}
              getValues={props.getValues}
              errorText={t('please-choose-a-county')}
              touched={props.touchedFields["billing-address-region"]}
              maskingClass='gb-mask'
              autocomplete={'region'}
              control={props.control}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-region")}
            />
          )}

            <Textfield
              register={props.register}
              errors={props.errors}
              inputSize={8}
              required={true}
              name={"billing-address-postal-code"}
              label={t('postcode')}
              errorText={t('please-enter-a-postcode')}
              setValue={props.setValue}
              touched={props.touchedFields["billing-address-postal-code"]}
              maskingClass='gb-mask'
              autocomplete={'postal-code'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-postal-codes")}
            />

            <Select
              control={props.control}
              register={props.register}
              errors={props.errors}
              inputSize={32}
              required={true}
              name={"billing-address-country"}
              label={t('country')}
              options={countries}
              setValue={props.setValue}
              onChange={handleCountryBillingChange}
              initialOption={selectedCountryBilling}
              getValues={props.getValues}
              errorText={t('please-choose-a-country')}
              touched={props.touchedFields["billing-address-country"]}
              maskingClass='gb-mask'
              autocomplete={'country-name'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-country")}
            />

            {!isCro1381 &&
              <Textfield
                register={props.register}
                errors={props.errors}
                inputSize={32}
                required={true}
                name={"billing-address-phone"}
                label={t('phone-number')}
                errorText={t('please-enter-a-phone-number')}
                placeholder={t('phone-number-placeholder')}
                minLength={8}
                pattern={/^[0-9]+$/}
                setValue={props.setValue}
                touched={props.touchedFields["billing-address-phone"]}
                maskingClass='gb-mask'
                autocomplete={'tel'}
                dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-phone")}
              />
            }
          </div>
        </div>
      </StandardFieldset>
    </React.Fragment>
  );
};

export default BillingAddress;
